import React, { useState } from 'react';
import { TrendingDownIcon, DollarSignIcon, UsersIcon, CalculatorIcon } from 'lucide-react';
export function SavingsEstimator() {
  const [staffCount, setStaffCount] = useState(10);
  const [hoursPerWeek, setHoursPerWeek] = useState(40);
  const [currentAgencyRate, setCurrentAgencyRate] = useState(55);
  const careNomadRate = 38;
  const savingsPerHour = currentAgencyRate - careNomadRate;
  const weeklySavings = savingsPerHour * hoursPerWeek * staffCount;
  const monthlySavings = weeklySavings * 4.33;
  const annualSavings = weeklySavings * 52;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  }).format(amount);
  const savingsPercent = Math.round(savingsPerHour / currentAgencyRate * 100);
  return <section className="w-full bg-white py-16 lg:py-24" aria-label="Staffing savings estimator" data-id="element-299">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-300">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-301">
          {/* Left: Copy */}
          <div data-id="element-302">
            <div className="inline-flex items-center gap-2 bg-soft-teal text-accent-teal-dark text-xs font-semibold px-3 py-1.5 rounded-full mb-4" data-id="element-303">
              <CalculatorIcon className="w-3.5 h-3.5" data-id="element-304" />
              Savings Estimator
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-text-dark tracking-tight mb-4" data-id="element-305">
              See How Much Your Facility{' '}
              <span className="text-medical-blue" data-id="element-306">Could Save</span>
            </h2>
            <p className="text-text-muted text-lg leading-relaxed mb-8" data-id="element-307">
              Traditional staffing agencies charge 40–60% markups. Care Nomad's
              direct marketplace model cuts out the middleman — passing savings
              directly to your facility.
            </p>

            <div className="mb-8 rounded-2xl overflow-hidden h-48 shadow-sm border border-gray-100" data-id="element-308">
              <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop" alt="Financial Savings" className="w-full h-full object-cover" data-id="element-309" />
            </div>

            {/* Feature list */}
            <div className="space-y-4" data-id="element-310">
              <div className="flex items-start gap-3" data-id="element-311">
                <div className="w-8 h-8 bg-soft-teal rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-312">
                  <TrendingDownIcon className="w-4 h-4 text-accent-teal" data-id="element-313" />
                </div>
                <div data-id="element-314">
                  <div className="font-semibold text-text-dark text-sm" data-id="element-315">
                    No Agency Markup
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-316">
                    Direct worker-to-facility matching eliminates inflated
                    agency fees
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-317">
                <div className="w-8 h-8 bg-soft-teal rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-318">
                  <DollarSignIcon className="w-4 h-4 text-accent-teal" data-id="element-319" />
                </div>
                <div data-id="element-320">
                  <div className="font-semibold text-text-dark text-sm" data-id="element-321">
                    Transparent Pricing
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-322">
                    See exact rates upfront — no hidden fees or surprise
                    invoices
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-323">
                <div className="w-8 h-8 bg-soft-teal rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-324">
                  <UsersIcon className="w-4 h-4 text-accent-teal" data-id="element-325" />
                </div>
                <div data-id="element-326">
                  <div className="font-semibold text-text-dark text-sm" data-id="element-327">
                    Workers Earn More Too
                  </div>
                  <div className="text-text-muted text-sm" data-id="element-328">
                    Higher take-home pay means better retention and quality of
                    care
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Calculator */}
          <div className="bg-white rounded-2xl shadow-float border border-gray-100 overflow-hidden" data-id="element-329">
            {/* Calculator header */}
            <div className="bg-medical-blue px-6 py-5" data-id="element-330">
              <h3 className="text-white font-bold text-lg" data-id="element-331">
                Staffing Cost Calculator
              </h3>
              <p className="text-white/70 text-sm mt-1" data-id="element-332">
                Adjust sliders to match your facility's needs
              </p>
            </div>

            <div className="p-6 space-y-6" data-id="element-333">
              {/* Slider: Staff Count */}
              <div data-id="element-334">
                <div className="flex justify-between items-center mb-2" data-id="element-335">
                  <label className="text-sm font-semibold text-text-dark" data-id="element-336">
                    Number of Staff
                  </label>
                  <span className="text-medical-blue font-bold text-lg" data-id="element-337">
                    {staffCount}
                  </span>
                </div>
                <input type="range" min={1} max={50} value={staffCount} onChange={e => setStaffCount(Number(e.target.value))} className="w-full" aria-label="Number of staff" aria-valuemin={1} aria-valuemax={50} aria-valuenow={staffCount} data-id="element-338" />
                <div className="flex justify-between text-xs text-text-muted mt-1" data-id="element-339">
                  <span data-id="element-340">1 staff</span>
                  <span data-id="element-341">50 staff</span>
                </div>
              </div>

              {/* Slider: Hours per week */}
              <div data-id="element-342">
                <div className="flex justify-between items-center mb-2" data-id="element-343">
                  <label className="text-sm font-semibold text-text-dark" data-id="element-344">
                    Hours per Week (per staff)
                  </label>
                  <span className="text-medical-blue font-bold text-lg" data-id="element-345">
                    {hoursPerWeek}h
                  </span>
                </div>
                <input type="range" min={8} max={80} step={4} value={hoursPerWeek} onChange={e => setHoursPerWeek(Number(e.target.value))} className="w-full" aria-label="Hours per week per staff" aria-valuemin={8} aria-valuemax={80} aria-valuenow={hoursPerWeek} data-id="element-346" />
                <div className="flex justify-between text-xs text-text-muted mt-1" data-id="element-347">
                  <span data-id="element-348">8 hrs</span>
                  <span data-id="element-349">80 hrs</span>
                </div>
              </div>

              {/* Slider: Current agency rate */}
              <div data-id="element-350">
                <div className="flex justify-between items-center mb-2" data-id="element-351">
                  <label className="text-sm font-semibold text-text-dark" data-id="element-352">
                    Current Agency Rate ($/hr)
                  </label>
                  <span className="text-medical-blue font-bold text-lg" data-id="element-353">
                    ${currentAgencyRate}/hr
                  </span>
                </div>
                <input type="range" min={30} max={90} step={1} value={currentAgencyRate} onChange={e => setCurrentAgencyRate(Number(e.target.value))} className="w-full" aria-label="Current agency rate per hour" aria-valuemin={30} aria-valuemax={90} aria-valuenow={currentAgencyRate} data-id="element-354" />
                <div className="flex justify-between text-xs text-text-muted mt-1" data-id="element-355">
                  <span data-id="element-356">$30/hr</span>
                  <span data-id="element-357">$90/hr</span>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" data-id="element-358" />

              {/* Results */}
              <div className="bg-soft-teal rounded-xl p-5" data-id="element-359">
                <div className="text-center mb-4" data-id="element-360">
                  <div className="text-xs font-semibold text-accent-teal-dark uppercase tracking-widest mb-1" data-id="element-361">
                    Your Estimated Annual Savings
                  </div>
                  <div className="text-5xl font-extrabold text-medical-blue" data-id="element-362">
                    {formatCurrency(annualSavings)}
                  </div>
                  <div className="text-sm text-accent-teal-dark font-medium mt-1" data-id="element-363">
                    Save up to {savingsPercent}% vs. traditional agencies
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3" data-id="element-364">
                  <div className="bg-white rounded-xl p-3 text-center" data-id="element-365">
                    <div className="text-xl font-extrabold text-text-dark" data-id="element-366">
                      {formatCurrency(weeklySavings)}
                    </div>
                    <div className="text-xs text-text-muted font-medium mt-0.5" data-id="element-367">
                      Per Week
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-3 text-center" data-id="element-368">
                    <div className="text-xl font-extrabold text-text-dark" data-id="element-369">
                      {formatCurrency(monthlySavings)}
                    </div>
                    <div className="text-xs text-text-muted font-medium mt-0.5" data-id="element-370">
                      Per Month
                    </div>
                  </div>
                </div>
              </div>

              {/* Rate comparison */}
              <div className="flex items-center justify-between text-sm" data-id="element-371">
                <div className="text-center" data-id="element-372">
                  <div className="text-text-muted text-xs mb-1" data-id="element-373">
                    Agency Rate
                  </div>
                  <div className="text-red-500 font-bold text-lg line-through" data-id="element-374">
                    ${currentAgencyRate}/hr
                  </div>
                </div>
                <div className="text-2xl text-text-muted" data-id="element-375">→</div>
                <div className="text-center" data-id="element-376">
                  <div className="text-text-muted text-xs mb-1" data-id="element-377">
                    Care Nomad Rate
                  </div>
                  <div className="text-accent-teal font-bold text-lg" data-id="element-378">
                    ${careNomadRate}/hr
                  </div>
                </div>
                <div className="text-center" data-id="element-379">
                  <div className="text-text-muted text-xs mb-1" data-id="element-380">You Save</div>
                  <div className="text-medical-blue font-bold text-lg" data-id="element-381">
                    ${savingsPerHour}/hr
                  </div>
                </div>
              </div>

              <button className="w-full bg-medical-blue hover:bg-medical-blue-dark text-white font-bold py-3.5 rounded-xl transition-colors" data-id="element-382">
                Get a Custom Quote →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}